/**
 * FollowUpsDuePanel — Today's "Follow-ups due" list, the bulk of its KEEP
 * TRYING group: the attention tier-2 work
 * (backend services/crmAttention.js; owner 2026-09-28: "the lower tier of
 * importance is trying to create a first response from an older lead by
 * repeating attempts"). Never-contacted leads that are not hot come first,
 * then cadence follow-ups due *today* (incl. overdue) to people who have not
 * answered us: no answer yet, or quiet since before the conversation window.
 * Live conversations and hot new leads are Today's needs-action sections, so
 * the server leaves them out of this list. It sits below the needs-action
 * sections and starts COLLAPSED (the choice is remembered): it is there when
 * the urgent work is done, never in the way of it.
 *
 * Why it exists: the worklist's follow_ups bucket only shows next_action_at<=now
 * (already overdue) and only mine+unassigned, so a follow-up scheduled for later
 * today — or owned by a teammate — never appeared on Today. This panel asks the
 * server for the whole local day's due follow-ups (GET /api/crm/follow-ups),
 * team-wide by default, and lets the recruiter sort (most overdue / oldest lead
 * / most attempts / name) and filter (study / recruiter / stage) client-side
 * over the fetched set. Read-only: rows open the lead like a real link (Ctrl/Cmd/
 * middle-click and right-click open a new tab — useRowNav + RowLink). Sort, scope,
 * and filter selections persist across navigation. Refetches on the same rhythm
 * as the rest of Today (crm:stats-changed + a 60s poll); a monotonic request
 * guard keeps a slow response from repainting under a newer scope.
 *
 * Layout: one filter definition list drives two renderings. From `sm` up the
 * selects sit inline exactly as before; on phones they collapse into a single
 * row (sort + a "Filters" popover + refresh) with applied values summarized as
 * dismissible chips — five wrapping selects were stacking three rows deep and
 * pushing the actual follow-ups off the screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlarmClock, ChevronDown, RefreshCw, User, Users } from 'lucide-react';
import api from '../../services/api';
import { ContactListItem, FollowUpsDueResponse, contactName } from '../types';
import { INCOMING_ELIGIBILITY_OPTIONS, STATS_CHANGED_EVENT, lifecycleMeta, relTime, usePersistedState, useTeam } from '../lib';
import {
  ActiveFilters, AddFilterMenu, FilterMultiSelect, FilterSelect, SegmentedControl, iconBtnCls,
  type ActiveChip, type FilterDef,
} from './filters';
import { StudyChips, RowLink, useRowNav } from './shared';
import CoverageBadge, { coverageOf } from './CoverageBadge';
import { Alert } from './ui';
import { touchTarget } from './ui/buttons';

const chipBase = 'inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium';

type SortKey = 'due' | 'age' | 'attempts' | 'eligibility' | 'name';

const SORT_OPTIONS = [
  { value: 'due', label: 'Most overdue' },
  { value: 'age', label: 'Oldest lead' },
  { value: 'attempts', label: 'Most attempts' },
  { value: 'eligibility', label: 'Eligibility' },
  { value: 'name', label: 'Name A–Z' },
];

// Eligibility order/rank — eligible first, then needs_review, pending,
// not_eligible (the INCOMING_ELIGIBILITY_OPTIONS order the inbox sorts by);
// null/unknown sorts last.
const ELIG_RANK = new Map(INCOMING_ELIGIBILITY_OPTIONS.map((o, i) => [o.value, i]));
const eligRank = (v: string | null | undefined): number =>
  ELIG_RANK.has(v || '') ? (ELIG_RANK.get(v || '') as number) : 99;

const SCOPE_OPTIONS = [
  { value: 'mine', label: 'Mine', icon: <User className="h-3.5 w-3.5" />, title: 'My follow-ups + unassigned' },
  { value: 'everyone', label: 'Everyone', icon: <Users className="h-3.5 w-3.5" />, title: "Everyone's follow-ups due today" },
];

interface Filters { study: string[]; owner: string[]; stage: string[]; elig: string[]; }
const EMPTY_FILTERS: Filters = { study: [], owner: [], stage: [], elig: [] };

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** End of the viewer's local day, as ISO — the "due today" upper bound. */
function endOfLocalTodayIso(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

/** "due now" / "due 3h ago" when past; "due in 2h" when later today. */
function dueChipFor(iso: string | null | undefined): { label: string; overdue: boolean } {
  if (!iso) return { label: 'due —', overdue: false };
  const t = new Date(iso).getTime();
  const overdue = !Number.isNaN(t) && t <= Date.now();
  const rel = relTime(iso); // past -> "3h"; future -> "in 2h"
  if (overdue) return { label: rel === 'now' ? 'due now' : `due ${rel} ago`, overdue: true };
  return { label: `due ${rel}`, overdue: false };
}

interface FollowUpsDuePanelProps {
  /** Reports the count of follow-ups due (data.count) on every load, so the
   *  Today page can keep its "all caught up" card consistent with this panel —
   *  later-today / teammates' follow-ups don't reach the worklist's bucket. */
  onCount?: (n: number) => void;
}

export default function FollowUpsDuePanel({ onCount }: FollowUpsDuePanelProps) {
  const navigate = useNavigate();
  const rowNav = useRowNav();
  const { byId } = useTeam();

  const [scope, setScope] = usePersistedState<'mine' | 'everyone'>('today.followups.scope', 'everyone');
  const [sort, setSort] = usePersistedState<SortKey>('today.followups.sort', 'due');
  // Key bumped to v2: the old pref held scalar filter fields; restoring those
  // strings into the now array-typed state would break the .includes() checks.
  const [filters, setFilters] = usePersistedState<Filters>('today.followups.filtersv2', EMPTY_FILTERS);
  const [data, setData] = useState<FollowUpsDueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Collapsed until the recruiter opens it (keep-trying work waits for the urgent work).
  const [expanded, setExpanded] = usePersistedState<boolean>('today.keeptrying.expanded', false);
  const reqRef = useRef(0); // latest-wins: a stale response must not repaint

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const myReq = ++reqRef.current;
    try {
      const res = await api.get('/api/crm/follow-ups', {
        params: { scope, due_before: endOfLocalTodayIso() },
      });
      if (myReq !== reqRef.current) return;
      setData(res.data as FollowUpsDueResponse);
      setError(null);
    } catch (e: any) {
      if (myReq !== reqRef.current) return;
      setError(errMsg(e));
    } finally {
      if (myReq === reqRef.current) setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
    const silent = () => load(true);
    const timer = window.setInterval(silent, 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, silent);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, silent);
    };
  }, [load]);

  // Lift the true due count (not the rendered/filtered length) so Today can
  // suppress its all-clear card whenever there is follow-up work to do.
  useEffect(() => {
    if (data) onCount?.(data.count);
  }, [data, onCount]);

  const items = useMemo(() => data?.items || [], [data]);
  const maxAttempts = data?.max_attempts || 6;

  // Filter option lists, derived from the loaded set (a filter with <2 distinct
  // values is hidden — nothing to choose).
  const studyOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of items) {
      for (const s of c.studies || []) {
        if (s.study_slug) m.set(s.study_slug, s.study_internal_title || s.study_title || s.study_slug);
      }
    }
    return [...m.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const ownerOptions = useMemo(() => {
    const ids = new Set<string>();
    let hasUnassigned = false;
    for (const c of items) {
      if (c.owner_user_id) ids.add(c.owner_user_id);
      else hasUnassigned = true;
    }
    const opts = [...ids]
      .map((id) => ({ value: id, label: byId.get(id)?.name || 'Teammate' }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (hasUnassigned) opts.push({ value: 'unassigned', label: 'Unassigned' });
    return opts;
  }, [items, byId]);

  const stageOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of items) if (c.lifecycle_status) set.add(c.lifecycle_status);
    return [...set]
      .map((v) => ({ value: v, label: lifecycleMeta(v).label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  // Eligibility verdicts present in the set, in canonical order (reuses the
  // inbox's labels so "Eligible" means the same thing here as there).
  const eligOptions = useMemo(() => {
    const present = new Set(items.map((c) => c.incoming_eligibility).filter(Boolean));
    return INCOMING_ELIGIBILITY_OPTIONS.filter((o) => present.has(o.value));
  }, [items]);

  // Apply filters, then sort — client-side over the day's fetched set.
  const visible = useMemo(() => {
    let rows = items;
    // Each filter: empty = no constraint; otherwise OR across the selected values.
    if (filters.study.length) rows = rows.filter((c) => (c.studies || []).some((s) => !!s.study_slug && filters.study.includes(s.study_slug)));
    if (filters.owner.length) {
      const wantUnassigned = filters.owner.includes('unassigned');
      rows = rows.filter((c) =>
        (c.owner_user_id && filters.owner.includes(c.owner_user_id)) || (wantUnassigned && !c.owner_user_id));
    }
    if (filters.stage.length) rows = rows.filter((c) => !!c.lifecycle_status && filters.stage.includes(c.lifecycle_status));
    if (filters.elig.length) rows = rows.filter((c) => !!c.incoming_eligibility && filters.elig.includes(c.incoming_eligibility));
    const sorted = [...rows];
    sorted.sort((a, b) => {
      switch (sort) {
        case 'age':
          return (a.created_at || '').localeCompare(b.created_at || '');
        case 'attempts':
          return (b.attempt_count || 0) - (a.attempt_count || 0)
            || (a.next_action_at || '').localeCompare(b.next_action_at || '');
        case 'eligibility':
          return eligRank(a.incoming_eligibility) - eligRank(b.incoming_eligibility)
            || (a.next_action_at || '').localeCompare(b.next_action_at || '');
        case 'name':
          return contactName(a).localeCompare(contactName(b));
        case 'due':
        default:
          return (a.next_action_at || '').localeCompare(b.next_action_at || '');
      }
    });
    return sorted;
  }, [items, filters, sort]);

  const filtersActive = !!(filters.study.length || filters.owner.length || filters.stage.length || filters.elig.length);
  const patchFilters = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));

  // The four filters in one table, so the inline row (>= sm), the phone
  // popover, and the chip summary can't drift apart. `min` is how many distinct
  // values the loaded set must offer before the filter is worth showing —
  // eligibility is 1 (vs 2): isolating the one verdict from no-verdict leads is
  // still useful, and it must stay reachable to clear a stale persisted value.
  const filterMeta = useMemo(() => ([
    { key: 'study' as const, label: 'Study', any: 'All studies', min: 2, options: studyOptions, fallback: (v: string) => v },
    { key: 'owner' as const, label: 'Recruiter', any: 'All recruiters', min: 2, options: ownerOptions, fallback: (v: string) => (v === 'unassigned' ? 'Unassigned' : byId.get(v)?.name || 'Teammate') },
    { key: 'stage' as const, label: 'Stage', any: 'All stages', min: 2, options: stageOptions, fallback: (v: string) => lifecycleMeta(v).label },
    { key: 'elig' as const, label: 'Eligibility', any: 'Any eligibility', min: 1, options: eligOptions, fallback: (v: string) => INCOMING_ELIGIBILITY_OPTIONS.find((o) => o.value === v)?.label || v },
  ]), [studyOptions, ownerOptions, stageOptions, eligOptions, byId]);

  const filterDefs = useMemo<FilterDef[]>(
    () => filterMeta
      .filter((m) => m.options.length >= m.min)
      .map((m) => ({
        type: 'multiselect',
        key: m.key,
        label: m.label,
        values: filters[m.key],
        options: [{ value: '', label: m.any }, ...m.options],
        onChange: (v: string[]) => patchFilters({ [m.key]: v }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterMeta, filters],
  );

  // Applied values as dismissible chips — on phones the selects live behind the
  // popover, so this is the only place the applied state is visible. Built from
  // ALL four filters, not just the shown ones: a persisted value whose filter
  // has dropped below `min` still needs a way out.
  const activeChips = useMemo<ActiveChip[]>(
    () => filterMeta.flatMap((m) => filters[m.key].map((v) => ({
      key: `${m.key}-${v}`,
      label: `${m.label}: ${m.options.find((o) => o.value === v)?.label || m.fallback(v)}`,
      onRemove: () => patchFilters({ [m.key]: filters[m.key].filter((x) => x !== v) }),
    }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterMeta, filters],
  );

  // Quiet on the very first load (siblings convention); a hard error with no
  // data shows a small retry so the user isn't left wondering where it went.
  if (loading && !data) return null;
  if (!data) {
    return error ? (
      <Alert tone="error" icon={false}>
        Couldn't load follow-ups.{' '}
        <button onClick={() => load()} className="font-medium underline hover:no-underline">Retry</button>
      </Alert>
    ) : null;
  }
  // Nothing due and not actively filtering → render nothing. Matches the Today
  // sections' quiet-when-empty convention and avoids a redundant empty card
  // stacked under the page's "all caught up" state. (When a filter is active we
  // stay visible so the recruiter can see "no match" and clear it.)
  if (items.length === 0 && !filtersActive) return null;

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className={`flex ${touchTarget} min-w-0 flex-1 items-center gap-2 rounded-lg text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-700/50`}
        >
          <AlarmClock className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
          {/* "Follow-ups due", not "Keep trying": Today already heads the group it sits in that way. */}
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">Follow-ups due</span>
          <span className="flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">
            ({filtersActive ? `${visible.length}/${items.length}` : data.count})
          </span>
          <span className="hidden truncate text-xs text-gray-400 md:block">
            No answer yet, gone quiet, or never contacted
          </span>
          <ChevronDown
            className={`ml-auto h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
        <SegmentedControl
          label="Follow-up scope"
          value={scope}
          onChange={(v) => setScope(v as 'mine' | 'everyone')}
          options={SCOPE_OPTIONS}
        />
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-3 dark:border-gray-700">
          {/* Phones: sort + a single "Filters" popover (a bottom sheet at this
              width) + refresh, on one row. Applied values ride the chip row
              below, so nothing is hidden — it just stops eating the screen. */}
          <div className="flex items-center gap-2 py-3 sm:hidden">
            <FilterSelect
              auto
              label="Sort follow-ups"
              value={sort}
              onChange={(v) => setSort(v as SortKey)}
              options={SORT_OPTIONS}
              className="min-w-0 flex-1"
            />
            {filterDefs.length > 0 && (
              <div className="flex-shrink-0">
                <AddFilterMenu filters={filterDefs} activeCount={activeChips.length} label="Filters" />
              </div>
            )}
            <button
              onClick={() => load()}
              disabled={loading}
              aria-label="Refresh follow-ups"
              className={`${iconBtnCls} flex-shrink-0`}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="sm:hidden">
            <ActiveFilters chips={activeChips} onClearAll={filtersActive ? () => setFilters(EMPTY_FILTERS) : undefined} />
          </div>

          {/* Desktop: the selects inline, where there is room for them. */}
          <div className="hidden flex-wrap items-center gap-2 py-3 sm:flex">
            <FilterSelect auto label="Sort follow-ups" value={sort} onChange={(v) => setSort(v as SortKey)} options={SORT_OPTIONS} />
            {filterDefs.map((def) => def.type === 'multiselect' && (
              <FilterMultiSelect
                key={def.key}
                auto
                label={def.label}
                values={def.values}
                onChange={def.onChange}
                options={def.options}
              />
            ))}
            {filtersActive && (
              <button
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-xs font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Clear filters
              </button>
            )}
            <button
              onClick={() => load()}
              disabled={loading}
              aria-label="Refresh follow-ups"
              className={`${iconBtnCls} ml-auto`}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {visible.length === 0 ? (
            <p className="py-3 text-sm text-gray-500 dark:text-gray-400">
              {items.length === 0 ? 'Nobody to keep trying today.' : 'Nobody here matches these filters.'}
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {visible.map((c: ContactListItem) => {
                const meta = lifecycleMeta(c.lifecycle_status);
                const owner = c.owner_user_id ? byId.get(c.owner_user_id) : null;
                const neverContacted = !c.attempt_count;
                const due = neverContacted ? { label: 'first try due', overdue: true } : dueChipFor(c.next_action_at);
                const attemptNo = Math.min((c.attempt_count || 0) + 1, maxAttempts);
                const url = `/crm/contacts/${c.id}`;
                // What kind of keep-trying row this is, in the recruiter's words.
                const context = neverContacted
                  ? `Never contacted · came in ${relTime(c.created_at)} ago`
                  : c.last_inbound_at
                    ? `Last wrote ${relTime(c.last_inbound_at)} ago · last touch ${relTime(c.last_outbound_at || c.last_activity_at)}`
                    : `No answer yet · attempt ${attemptNo} of ${maxAttempts} · last touch ${relTime(c.last_outbound_at || c.last_activity_at)}`;
                const dueCls = due.overdue
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
                return (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    {...rowNav(url)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(url); }}
                    className="flex cursor-pointer items-center gap-3 py-3 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <RowLink to={url} title={contactName(c)} className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {contactName(c)}
                        </RowLink>
                        <span className={`${chipBase} ${meta.badge}`}>{meta.label}</span>
                        {!c.owner_user_id && (
                          <span className={`${chipBase} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}>Unassigned</span>
                        )}
                        {/* Coverage wins over the plain owner chip: while an
                            owner is out, "Dana" alone reads as "Dana's got it"
                            — the opposite of what it means here. */}
                        {coverageOf(c)
                          ? <CoverageBadge coverage={coverageOf(c)!} />
                          : owner && (
                            <span className={`${chipBase} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}>{owner.name}</span>
                          )}
                        {/* Phone: the due chip ends the name line so the context
                            and study chips run the row's width; desktop keeps it
                            at the right (stateless, one copy per tier). */}
                        <span className={`${chipBase} ml-auto sm:hidden ${dueCls}`}>{due.label}</span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                        {context}
                      </p>
                      <div className="mt-1">
                        <StudyChips studies={c.studies} />
                      </div>
                    </div>
                    <span className={`${chipBase} hidden sm:inline-flex ${dueCls}`}>
                      {due.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {data.truncated && (
            <p className="mt-2 text-xs text-gray-400">
              {filtersActive
                ? `Filtering the first ${items.length} of ${data.count} — matches beyond this cap aren't shown; work them down or use the inbox.`
                : `Showing the first ${items.length} of ${data.count} — work these down or use the inbox to see the rest.`}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
